import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * <GridMap> instances each MeshLibrary item's mesh, an ArrayMesh or a PrimitiveMesh, at its
 * populated cells. Each surface draws as one THREE.InstancedMesh, or as one THREE.Points instanced
 * at every cell for a PointMesh. A billboarding material turns each cell about its own origin in its
 * program. A loading item draws nothing.
 * An item whose mesh or library this previewer cannot read draws cell-sized wireframe boxes in one
 * InstancedMesh.
 */
import { useLayoutEffect, useMemo, useRef } from 'react';
import { GODOT_DEFAULT_VISUAL_LAYERS } from '../../../godot/scene/visualLayers';
import * as THREE from 'three';
import { nodeComponentRegistry } from '../../../r3f/NodeComponentRegistry';
import { parseNodeWithRegistry } from '../../../core/NodeRegistry';
import { LightmapSubInstanceProvider } from '../../../r3f/lightmap/useLightmapAssignment';
import { Node3D } from '../../base/node3d/Component';
import { useMeshLibraryModel } from '../../../r3f/useMeshLibraryModel';
import { useMeshContent } from '../../../r3f/meshes/meshContent';
import { geometryDrawsPoints } from '../../../resources/meshes/godotSurface';
import { usePathResolution } from '../../../resources/useSubOrExtResource';
import { SurfaceMaterialSlot } from '../../../r3f/materials/SurfaceMaterialSlot';
import { surfaceAttach, surfaceSources } from '../../../r3f/materials/SurfaceMaterialSlots';
import { useOneSurfaceStartingMaterial } from '../../../r3f/materials/useOneSurfaceStartingMaterial';
import { NextPassChain, nextPassRenderOrder, } from '../../../r3f/materials/nextPass';
import { NO_RAYCAST } from '../../../r3f/materials/materialOverlay';
import { decodeGridMapCells, ORTHO_BASES } from './cellData';
import { wireGizmoProgram } from '../../../r3f/components/wireGizmoProgram';
import { instancesBounds, pointPlacementOf, raycastInstancedPoints, sharedInstancesAt, useSharedInstances, } from '../../../r3f/meshes/sharedInstances';
import { drawHookProps, shadowCastingEffects } from '../../../r3f/shadowCasting';
import { transform3DToMatrix } from '../../../r3f/nodeTreeTransforms';
import { isLocalToScene } from '../../../resources/textures/viewporttexture/localToScene.js';
/** Literal-only, so the key is constant and the placeholder batch never remounts its material. */
const PLACEHOLDER_TILE_MATERIAL = wireGizmoProgram(0x4488cc);
/** Written only by `cellMatrix`. `Matrix4.scale` reads it at once, so a large grid allocates none. */
const cellScaleVector = new THREE.Vector3();
/**
 * A cell's place: translate(cell·size + centering offset). The offset is Godot's `_get_offset()`:
 * `cell_size * 0.5` on each axis whose `cell_center_*` is on, all three by default. Without it the
 * tiles sit half a cell off from sibling nodes.
 */
function cellOrigin(cell, { cellSize, cellCenter }) {
    return new THREE.Matrix4().makeTranslation(cell.x * cellSize.x + (cellCenter.x ? cellSize.x * 0.5 : 0), cell.y * cellSize.y + (cellCenter.y ? cellSize.y * 0.5 : 0), cell.z * cellSize.z + (cellCenter.z ? cellSize.z * 0.5 : 0));
}
/**
 * World matrix for one cell: its place × scale(cell_scale) × orientation × meshTransform
 * (`modules/gridmap/grid_map.cpp:685-687,696`).
 */
function cellMatrix(cell, grid, meshTransform) {
    const basis = ORTHO_BASES[cell.rot] ?? ORTHO_BASES[0];
    const orient = new THREE.Matrix4().set(basis[0], basis[1], basis[2], 0, basis[3], basis[4], basis[5], 0, basis[6], basis[7], basis[8], 0, 0, 0, 0, 1);
    const matrix = cellOrigin(cell, grid);
    matrix.scale(cellScaleVector.setScalar(grid.cellScale)).multiply(orient);
    if (meshTransform)
        matrix.multiply(transform3DToMatrix(meshTransform));
    return matrix;
}
export function GridMap({ node, children }) {
    const properties = node.properties;
    const library = useMeshLibraryModel(properties.meshLibrary);
    const isMeshHeldLocally = !library.resource || isLocalToScene(library.resource);
    const cellsByItem = useMemo(() => {
        const grouped = new Map();
        for (const cell of decodeGridMapCells(properties.cells ?? '')) {
            const list = grouped.get(cell.item);
            if (list)
                list.push(cell);
            else
                grouped.set(cell.item, [cell]);
        }
        return grouped;
    }, [properties.cells]);
    // Godot draws no cell while the GridMap holds baked meshes (`modules/gridmap/grid_map.cpp:688`).
    const hasBakedMeshes = properties.bakedMeshes.length > 0;
    const items = library.status === 'pending' || hasBakedMeshes ? [] : Array.from(cellsByItem.entries());
    const gridProps = {
        cellSize: properties.cellSize,
        cellCenter: properties.cellCenter,
        cellScale: properties.cellScale,
    };
    return (_jsxs(Node3D, { node: node, children: [hasBakedMeshes && _jsx(BakedMeshes, { refs: properties.bakedMeshes }), items.map(([itemId, cells]) => {
                // A library this previewer cannot load draws each cell's placeholder instead.
                if (!library.model)
                    return _jsx(PlaceholderTiles, { cells: cells, ...gridProps }, itemId);
                const item = library.model.get(itemId);
                // Godot draws a cell only for an item the library holds with a mesh (`:676-678,689`).
                if (!item?.meshPath)
                    return null;
                return (_jsx(GridMapItem, { item: item, isMeshHeldLocally: isMeshHeldLocally, cells: cells, ...gridProps }, itemId));
            }), children] }));
}
/**
 * Each baked mesh as the instance Godot gives it at the GridMap's own transform
 * (`grid_map.cpp:97-104`, `:1037-1039`), drawn as a MeshInstance3D of that mesh would be. A
 * LightmapGI names each by its index (`lightmap_gi.cpp:1544-1550`).
 */
function BakedMeshes({ refs }) {
    const nodes = useMemo(() => refs.map(bakedMeshNode), [refs]);
    const MeshInstance3D = nodeComponentRegistry.get(MESH_INSTANCE_TYPE);
    if (!MeshInstance3D)
        return null;
    return (_jsx(_Fragment, { children: nodes.map((meshNode, index) => (_jsx(LightmapSubInstanceProvider, { value: index, children: _jsx(MeshInstance3D, { node: meshNode }) }, index))) }));
}
const MESH_INSTANCE_TYPE = 'MeshInstance3D';
/** A MeshInstance3D of the baked mesh `ref`, with every other property at its default. */
function bakedMeshNode(ref, index) {
    const heading = { type: 'node', attributes: { type: MESH_INSTANCE_TYPE, name: `BakedMesh${index}` } };
    const node = parseNodeWithRegistry(heading, { mesh: ref });
    if (!node)
        throw new Error(`expected the ${MESH_INSTANCE_TYPE} parser to be registered, got none`);
    return node;
}
/**
 * All cells sharing one MeshLibrary item, each surface as one instanced draw, which Godot
 * billboards per instance in its shader (`scene_forward_clustered.glsl:338-342`).
 */
function GridMapItem({ item, isMeshHeldLocally, cells, cellSize, cellCenter, cellScale }) {
    const content = useMeshContent(usePathResolution(item.meshPath), isMeshHeldLocally);
    // `cellCenter` is compared by identity: the parser hands back one shared
    // frozen instance for the all-centered default, so re-parsing an unchanged
    // file (every debounced keystroke) does not rebuild every cell's matrix.
    const matrices = useMemo(() => {
        const grid = { cellSize, cellCenter, cellScale };
        return cells.map((cell) => cellMatrix(cell, grid, item.meshTransform));
    }, [cells, cellSize, cellCenter, cellScale, item.meshTransform]);
    // GridMap has no `cast_shadow` of its own. The setting is per tile, read off
    // the library (`modules/gridmap/grid_map.cpp:799-800`).
    const shadow = shadowCastingEffects(item.castShadow);
    // Godot draws the mesh, so a mesh this previewer cannot read shows the placeholder.
    if (content.kind === 'unresolved') {
        return _jsx(PlaceholderTiles, { cells: cells, cellSize: cellSize, cellCenter: cellCenter });
    }
    if (content.kind !== 'surfaced')
        return null;
    const { parts } = content.mesh;
    return (_jsx(_Fragment, { children: parts.map((part, partIndex) => (_jsx(GridMapPart, { part: part, shadow: shadow, matrices: matrices }, partIndex))) }));
}
/** One part of an item's mesh at every cell: the surfaces that share one primitive type. */
function GridMapPart(props) {
    return geometryDrawsPoints(props.part.geometry) ? _jsx(PointsPart, { ...props }) : _jsx(PartSurfaces, { ...props });
}
/**
 * A points part at every cell as one instanced geometry over the item's own, shared by the part's
 * surfaces, new for each set of cells, with the box and sphere of every cell.
 */
function PointsPart(props) {
    const { part, matrices } = props;
    const cellPoints = useMemo(() => ({
        geometry: sharedInstancesAt(part.geometry, matrices),
        ...instancesBounds(part.geometry, matrices),
    }), [part.geometry, matrices]);
    useSharedInstances(cellPoints.geometry, part.geometry);
    return _jsx(PartSurfaces, { ...props, cellPoints: cellPoints });
}
function PartSurfaces({ part, cellPoints, shadow, matrices, }) {
    const surfaces = surfaceSources(part.materials);
    const { geometry } = part;
    const points = geometryDrawsPoints(geometry);
    // A cell's chain draw is its own instanced batch of the cells, at its surface's
    // ordinal, over the geometry the part draws, and at its surface's own draw group.
    const chainDraw = (spec) => cellPoints ? (_jsx(ChainCellPoints, { spec: spec, cellPoints: cellPoints })) : (_jsx(ChainBatchedTiles, { spec: spec, matrices: matrices }));
    return (_jsxs(_Fragment, { children: [surfaces.map((source, surface) => (_jsx(GridMapSurface, { draw: { geometry, cellPoints, source, surface, surfaceCount: surfaces.length, shadow }, matrices: matrices }, surface))), _jsx(NextPassChain, { sources: surfaces, geometry: cellPoints?.geometry ?? geometry, drawsPoints: points, godotLayers: GODOT_DEFAULT_VISUAL_LAYERS, shadow: shadow, draw: chainDraw })] }));
}
/** The chain link of a tile surface's material, as one more instanced batch of the cells. */
function ChainBatchedTiles({ spec, matrices }) {
    const startingMaterial = useOneSurfaceStartingMaterial(spec.surfaceCount);
    const meshRef = useRef(null);
    // Every arg is a dependency: a changed arg rebuilds the mesh with identity instance matrices.
    useLayoutEffect(() => writeInstanceMatrices(meshRef.current, matrices), [matrices, spec.geometry]);
    return (_jsx("instancedMesh", { ref: meshRef, args: [spec.geometry, startingMaterial, matrices.length], renderOrder: nextPassRenderOrder(spec.ordinal), 
        // The surface's own batch is the grid's pickable body; a coincident second hit
        // would report the same cells twice.
        raycast: NO_RAYCAST, receiveShadow: true, onBeforeRender: spec.shadow.onBeforeRender, onAfterRender: spec.shadow.onAfterRender, children: _jsx(SurfaceMaterialSlot, { source: spec.source, attach: spec.attach, points: pointPlacementOf(spec.geometry) }) }, matrices.length));
}
/** The chain link of a points surface's material, as one more draw of the cells' points. */
function ChainCellPoints({ spec, cellPoints }) {
    const { geometry, boundingBox, boundingSphere } = cellPoints;
    const startingMaterial = useOneSurfaceStartingMaterial(spec.surfaceCount);
    const pointsRef = useRef(null);
    // Typed props hold no box on THREE.Points, which three and the framing read off the object.
    useLayoutEffect(() => {
        if (pointsRef.current)
            Object.assign(pointsRef.current, { boundingBox, boundingSphere });
    }, [boundingBox, boundingSphere]);
    return (_jsx("points", { ref: pointsRef, args: [geometry, startingMaterial], renderOrder: nextPassRenderOrder(spec.ordinal), raycast: NO_RAYCAST, 
        // The chain draw casts nothing of its own: the surface's own draw casts the
        // geometry once, so only the colour pair mounts.
        receiveShadow: true, onBeforeRender: spec.shadow.onBeforeRender, onAfterRender: spec.shadow.onAfterRender, children: _jsx(SurfaceMaterialSlot, { source: spec.source, attach: spec.attach, points: "instances" }) }));
}
/**
 * Every cell of one surface as one instanced draw, a billboarding one too: its program billboards
 * each cell about its own origin, as Godot's multimesh does (`surfaceBillboard.ts`).
 */
function GridMapSurface({ draw, matrices }) {
    if (!draw.cellPoints)
        return _jsx(BatchedTiles, { draw: draw, matrices: matrices });
    return _jsx(InstancedCellPoints, { draw: draw, cellPoints: draw.cellPoints });
}
/**
 * Every cell as one InstancedMesh, keyed on the count: three sizes the instance buffer at
 * construction. `useMeshContent` owns the geometry, which R3F leaves alone since it arrives
 * through `args`. The shadow hooks carry DOUBLE_SIDED and SHADOWS_ONLY per draw
 * (`r3f/surfaceDrawHooks.ts`).
 */
function BatchedTiles({ draw, matrices }) {
    const { geometry, shadow } = draw;
    const startingMaterial = useOneSurfaceStartingMaterial(draw.surfaceCount);
    const meshRef = useRef(null);
    // Every arg is a dependency: a changed arg rebuilds the mesh with identity instance matrices.
    useLayoutEffect(() => writeInstanceMatrices(meshRef.current, matrices), [matrices, geometry, startingMaterial]);
    return (_jsx("instancedMesh", { ref: meshRef, args: [geometry, startingMaterial, matrices.length], ...drawHookProps(shadow), children: _jsx(OwnedSurfaceSlot, { draw: draw }) }, matrices.length));
}
/** A surface's points at every cell, one instanced THREE.Points picked at each cell. */
function InstancedCellPoints({ draw, cellPoints }) {
    const { geometry, boundingBox, boundingSphere } = cellPoints;
    const startingMaterial = useOneSurfaceStartingMaterial(draw.surfaceCount);
    const pointsRef = useRef(null);
    // Typed props hold no box on THREE.Points, which three and the framing read off the object.
    useLayoutEffect(() => {
        if (pointsRef.current)
            Object.assign(pointsRef.current, { boundingBox, boundingSphere });
    }, [boundingBox, boundingSphere]);
    return (_jsx("points", { ref: pointsRef, args: [geometry, startingMaterial], raycast: raycastInstancedPoints, ...drawHookProps(draw.shadow), children: _jsx(OwnedSurfaceSlot, { draw: { ...draw, geometry } }) }));
}
/** The material of the one draw group a surface draw owns. */
function OwnedSurfaceSlot({ draw }) {
    return (_jsx(SurfaceMaterialSlot, { source: draw.source, attach: surfaceAttach(draw.surface, draw.surfaceCount), points: pointPlacementOf(draw.geometry) }));
}
/**
 * A cell-sized wireframe box at each cell's place, all in one draw, so a missing item in a large
 * grid costs one draw as its tiles would.
 */
function PlaceholderTiles({ cells, cellSize, cellCenter, }) {
    const meshRef = useRef(null);
    const matrices = useMemo(() => cells.map((cell) => cellOrigin(cell, { cellSize, cellCenter })), [cells, cellSize, cellCenter]);
    useLayoutEffect(() => writeInstanceMatrices(meshRef.current, matrices), [matrices]);
    return (_jsxs("instancedMesh", { ref: meshRef, args: [undefined, undefined, matrices.length], children: [_jsx("boxGeometry", { args: [cellSize.x, cellSize.y, cellSize.z] }), _jsx("meshBasicMaterial", { ...PLACEHOLDER_TILE_MATERIAL.props }, PLACEHOLDER_TILE_MATERIAL.key)] }, matrices.length));
}
/** Sets each instance of `mesh` to its matrix. A mesh not yet mounted waits for the next call. */
function writeInstanceMatrices(mesh, matrices) {
    if (!mesh)
        return;
    matrices.forEach((m, i) => mesh.setMatrixAt(i, m));
    mesh.instanceMatrix.needsUpdate = true;
    // three computes these once and culls by them, so a moved cell would stay culled at its old place.
    mesh.boundingSphere = null;
    mesh.boundingBox = null;
}
//# sourceMappingURL=Component.js.map